import { BaseWidget, UnsafeWidgetUtilities, addEventListener } from "./widegt";
import { IIterator, toArray, map, ArrayExt } from '@phosphor/algorithm';
import { MessageLoop, Message } from "@phosphor/messaging";
import { Widget, SplitPanel, PanelLayout, SplitLayout, LayoutItem } from "@phosphor/widgets";
import { SplitPositionHandler, SplitPositionOptions } from "./split-panels";
import { Emitter, Event as CommonEvent } from './event';
import { parseCssMagnitude } from './browser';
import { DisposableCollection, Disposable } from "./disposable";
import { MimeData } from '@phosphor/coreutils';
import { TabBarToolbar } from '../nav-panel/tool-bar/tool-bar';

const ICON_CLASS_EXPANDED = "f-icon-arrow-chevron-up";
const ICON_CLASS_COLLAPSED = "f-icon-arrow-chevron-down";

export interface ViewContainerTitleOptions {
  label: string;
  caption?: string;
  iconClass?: string;
  closeable?: boolean;
}

export class ViewContainerIdentifier {
  id: string;
  progressLocationId?: string;
}

/** 收折面板配置 */
export interface ViewContainerPartConfig {
  /** 是否隐藏标题栏 */
  hideTitleBar?: boolean;
  /** 指定滚动条容器 */
  customScrollContainerEl?: HTMLElement;
}

export class ViewContainer extends BaseWidget {

  protected panel: SplitPanel;

  protected currentPart: ViewContainerPart | undefined;

  protected splitPositionHandler: SplitPositionHandler = new SplitPositionHandler();

  get containerLayout(): ViewContainerLayout {
    const layout = this.panel.layout;
    if (layout instanceof ViewContainerLayout) {
      return layout;
    }
    throw new Error('view container is disposed');
  }

  getParts(): ViewContainerPart[] {
    return this.containerLayout.widgets;
  }

  /** 更新各个面板的滚动条大小 */
  public updateAllScrollbar(): void {
    const parts = this.getParts();
    for (const part of parts) {
      part && part.updateScrollBar();
    }
  }

  protected getPartIndex(partId: string | undefined): number {
    if (partId) {
      return this.getParts().findIndex(part => part.id === partId);
    }
    return -1;
  }

  protected get enableAnimation(): boolean {
    return true;
  }

  constructor() {
    super();
    this.init();
  }

  private init(): void {
    this.addClass('farris-view-container');
    const layout = new PanelLayout();
    this.layout = layout;
    this.panel = new SplitPanel({
      layout: new ViewContainerLayout({
        renderer: SplitPanel.defaultRenderer,
        orientation: 'vertical',
        spacing: 2,
        headerSize: ViewContainerPart.HEADER_HEIGHT,
        animationDuration: 220
      }, this.splitPositionHandler)
    });
    this.panel.node.tabIndex = -1;
    this.configureLayout(layout);
  }

  protected configureLayout(layout: PanelLayout): void {
    layout.addWidget(this.panel);
  }

  protected toRemoveWidgets = new Map<string, DisposableCollection>();

  protected createPartId(widget: Widget): string {
    return widget.id;
  }

  protected createPart(
    widget: Widget,
    toolbar?: TabBarToolbar,
    config?: ViewContainerPartConfig
  ): ViewContainerPart {
    return new ViewContainerPart(widget, toolbar, config);
  }

  addWidget(widget: Widget, toolbar?: TabBarToolbar, config?: ViewContainerPartConfig): Disposable {
    const id = widget.id;
    if (!!id) {
      const existing = this.toRemoveWidgets.get(widget.id);
      if (existing) {
        return existing;
      }
    }
    const newPart = this.createPart(widget, toolbar, config);
    return this.attachNewPart(newPart);
  }

  protected attachNewPart(newPart: ViewContainerPart): Disposable {
    const newPartId = newPart.wrapped.id;
    const toRemoveWidget = new DisposableCollection();
    if (!!newPartId) {
      this.toDispose.push(toRemoveWidget);
      this.toRemoveWidgets.set(newPartId, toRemoveWidget);
      toRemoveWidget.push(Disposable.create(() => this.toRemoveWidgets.delete(newPartId)));
    }

    this.containerLayout.addWidget(newPart);

    this.updateCurrentPart();
    this.update();
    toRemoveWidget.pushAll([
      Disposable.create(() => {
        newPart.dispose();
        if (!newPart.isDisposed && this.getPartIndex(newPart.id) > -1) {
          this.containerLayout.removeWidget(newPart);
        }
        if (!this.isDisposed) {
          this.update();
          this.updateCurrentPart();
        }
      }),
      this.registerDND(newPart),
      newPart.onCollapsed(() => {
        this.containerLayout.updateCollapsed(newPart, this.enableAnimation);
        this.updateCurrentPart();
      })
    ]);

    newPart.disposed.connect(() => toRemoveWidget.dispose());
    return toRemoveWidget;
  }

  protected registerDND(part: ViewContainerPart): Disposable {
    part.headerElement.draggable = true;

    return new DisposableCollection(
      addEventListener(part.headerElement, 'dragstart',
        event => {
          event.preventDefault();
          const mimeData = new MimeData();
          mimeData.setData('application/vnd.phosphor.view-container-factory', () => part);
          const clonedHeader = part.headerElement.cloneNode(true) as HTMLElement;
          clonedHeader.style.width = part.node.style.width;
          clonedHeader.style.opacity = '0.6';
          part.node.classList.add('p-mod-hidden');
          setTimeout(() => { part.node.classList.remove('p-mod-hidden'); }, 0);
        }, false));
  }

  removeWidget(widget: Widget): boolean {
    const disposable = this.toRemoveWidgets.get(widget.id);
    if (disposable) {
      disposable.dispose();
      return true;
    }
    return false;
  }

  protected updateCurrentPart(part?: ViewContainerPart): void {
    if (part && this.getParts().indexOf(part) !== -1) {
      this.currentPart = part;
    }
    if (this.currentPart && !this.currentPart.isDisposed) {
      return;
    }
    const visibleParts = this.getParts().filter(p => !p.isHidden);
    const expandedParts = visibleParts.filter(p => !p.collapsed);
    this.currentPart = expandedParts[0] || visibleParts[0];
  }

  getPartFor(widget: Widget): ViewContainerPart | undefined {
    return this.getParts().find(p => p.wrapped.id === widget.id);
  }

  protected get orientation(): SplitLayout.Orientation {
    return 'vertical';
  }

  protected moveBefore(toMovedId: string, moveBeforeThisId: string): void {
    const parts = this.getParts();
    const toMoveIndex = parts.findIndex(part => part.id === toMovedId);
    const moveBeforeThisIndex = parts.findIndex(part => part.id === moveBeforeThisId);
    if (toMoveIndex >= 0 && moveBeforeThisIndex >= 0) {
      this.containerLayout.moveWidget(toMoveIndex, moveBeforeThisIndex, parts[toMoveIndex]);
      for (let index = Math.min(toMoveIndex, moveBeforeThisIndex); index < parts.length; index++) {
        this.activate();
      }
    }
  }

  getTrackableWidgets(): Widget[] {
    return this.getParts().map(w => w.wrapped);
  }

  activateWidget(id: string): Widget | undefined {
    const part = this.revealPart(id);
    if (!part) {
      return undefined;
    }
    this.updateCurrentPart(part);
    part.collapsed = false;
    return part.wrapped;
  }

  revealWidget(id: string): Widget | undefined {
    const part = this.revealPart(id);
    return part && part.wrapped;
  }

  protected revealPart(id: string): ViewContainerPart | undefined {
    const part = this.getParts().find(p => p.wrapped.id === id);
    if (!part) {
      return undefined;
    }
    part.setHidden(false);
    return part;
  }

  protected onActivateRequest(msg: Message): void {
    super.onActivateRequest(msg);
    if (this.currentPart) {
      this.currentPart.activate();
    } else {
      this.panel.node.focus({ preventScroll: true });
    }
  }

  protected onAfterAttach(msg: Message): void {
    const orientation = this.orientation;
    this.containerLayout.orientation = orientation;
    if (orientation === 'horizontal') {
      for (const part of this.getParts()) {
        part.collapsed = false;
      }
    }
    super.onAfterAttach(msg);
  }

  protected onBeforeHide(msg: Message): void {
    super.onBeforeHide(msg);
  }

  protected onAfterShow(msg: Message): void {
    super.onAfterShow(msg);
  }

  protected onBeforeAttach(msg: Message): void {
    super.onBeforeAttach(msg);
  }

  protected onAfterDetach(msg: Message): void {
    super.onAfterDetach(msg);
  }

}

export namespace ViewContainer {

  export interface State {
    title?: ViewContainerTitleOptions;
    parts: ViewContainerPart.State[]
  }

  export function getOrientation(): 'horizontal' | 'vertical' {
    return 'vertical';
  }

  /**
   * 整体上调整拖拽条显隐状态
   * @remarks
   * 1. 当该面板及其后续面板均收折时，隐藏该面板的拖拽条
   * 2. 当该面板上方的面板收折时，隐藏该面板的拖拽条
   * 3. 其余情况下显示该面板的拖拽条
   * @param layout 面板布局
   */
  export function updateSashStateOverall(layout: ViewContainerLayout): void {
    const parts = layout.widgets;
    const flags = [];  // 表示该节点及其后续节点中是否存在展开的节点
    flags.length = parts.length + 1;
    flags.fill(false);
    for (let i = parts.length - 1; i >= 0; --i) {
      if (!!flags[i + 1] || !parts[i].collapsed) {
        flags[i] = true;
      }
    }
    for (let i = 0; i < parts.length; ++i) {
      const showHandler = flags[i];
      const handler = parts[i].node && parts[i].node.previousElementSibling;
      if (!handler || !handler.className.includes('p-SplitPanel-handle')) {
        continue;
      }
      const lastPanel = i > 0 ? parts[i - 1] : null;
      if (showHandler && lastPanel && !lastPanel.collapsed) {
        handler.classList.toggle('sash-hidden', false);
      } else {
        handler.classList.toggle('sash-hidden', true);
      }
    }
  }
}

export class ViewContainerPart extends BaseWidget {
  protected readonly header: HTMLElement;
  protected toggleIconBtn: HTMLElement;
  protected readonly body: HTMLElement;
  protected readonly collapsedEmitter = new Emitter<boolean>();
  protected _collapsed: boolean;
  uncollapsedSize: number | undefined;
  animatedSize: number | undefined;
  wrapped: Widget;
  toolbar: TabBarToolbar;
  config?: ViewContainerPartConfig;

  constructor(
    wrapped: Widget,
    toolbar: TabBarToolbar,
    config?: ViewContainerPartConfig
  ) {
    super();
    this.config = config;
    this.wrapped = wrapped;
    this.toolbar = toolbar;
    wrapped.parent = this;
    wrapped.disposed.connect(() => this.dispose());

    this.addClass('part');
    if (this.config && this.config.hideTitleBar) {
      this.addClass('app-hide-header');
    }

    const { header, body, disposable } = this.createContent();
    this.header = header;
    this.body = body;
    this.toDispose.pushAll([
      disposable,
      this.collapsedEmitter,
      this.wrapped
    ]);
    this.scrollOptions = {
      suppressScrollX: true,
      minScrollbarLength: 35
    };
  }

  protected getScrollContainer(): HTMLElement {
    return ( this.config && this.config.customScrollContainerEl ) || this.body;
  }

  protected createContent(): { header: HTMLElement, body: HTMLElement, disposable: Disposable } {
    const disposable = new DisposableCollection();
    const { header, disposable: headerDisposable } = this.createHeader();
    const body = document.createElement('div');
    body.classList.add('split-panel-body');
    this.node.appendChild(header);
    this.node.appendChild(body);
    disposable.push(headerDisposable);
    return {
      header,
      body,
      disposable
    };
  }

  protected createHeader(): { header: HTMLElement, disposable: Disposable } {
    const disposable = new DisposableCollection();
    const header = document.createElement('div');
    header.tabIndex = 0;
    header.classList.add('split-panel-header', 'farris-view-container-part-header');
    disposable.push(addEventListener(header, 'click', event => {
      if (this.toolbar && this.toolbar.shouldHandleMouseEvent(event)) {
        return;
      }
      this.collapsed = !this.collapsed;
    }));

    // 新增面板标题
    const title = document.createElement('span');
    title.classList.add('header-title', 'noselect');
    const description = document.createElement('span');
    description.classList.add('header-description');
    const updateTitle = () => {
      title.innerText = this.wrapped.title.label;
    };
    const updateCaption = () => title.title = this.wrapped.title.caption || this.wrapped.title.label;
    updateTitle();
    updateCaption();
    header.appendChild(title);
    header.appendChild(description);

    // 新增收折按钮
    this.toggleIconBtn = document.createElement('button');
    this.toggleIconBtn.classList.add('p-panel-header-icon', 'p-panel-toggler');
    const toggleIcon = document.createElement('span');
    if (this.collapsed) {
      toggleIcon.classList.add('adjust-icon-toggle--nav-panel', 'f-icon', ICON_CLASS_COLLAPSED);
    } else {
      toggleIcon.classList.add('adjust-icon-toggle--nav-panel', 'f-icon', ICON_CLASS_EXPANDED);
    }
    this.toggleIconBtn.appendChild(toggleIcon);
    header.appendChild(this.toggleIconBtn);

    return {
      header,
      disposable
    };
  }

  get viewContainer(): ViewContainer | undefined {
    return this.parent ? this.parent.parent as ViewContainer : undefined;
  }

  get headerElement(): HTMLElement {
    return this.header;
  }

  get collapsed(): boolean {
    return this._collapsed;
  }

  set collapsed(collapsed: boolean) {
    if (this._collapsed === collapsed) {
      return;
    }
    this._collapsed = collapsed;
    this.node.classList.toggle('collapsed', collapsed);
    ViewContainer.updateSashStateOverall(this.viewContainer.containerLayout);

    if (collapsed && this.wrapped.node.contains(document.activeElement)) {
      this.header.focus();
    }
    this.wrapped.setHidden(collapsed);
    const toggleIconBtn = this.header.querySelector(`button.p-panel-toggler`);
    const toggleIcon = toggleIconBtn && toggleIconBtn.querySelector(`span.f-icon`);
    if (toggleIcon) {
      if (collapsed) {
        toggleIcon.classList.remove(ICON_CLASS_EXPANDED);
        toggleIcon.classList.add(ICON_CLASS_COLLAPSED);
      } else {
        toggleIcon.classList.remove(ICON_CLASS_COLLAPSED);
        toggleIcon.classList.add(ICON_CLASS_EXPANDED);
      }
    }
    this.update();
    this.collapsedEmitter.fire(collapsed);
  }

  setHidden(hidden: boolean): void {
    super.setHidden(hidden);
  }

  get canHide(): boolean {
    return true;
  }

  get onCollapsed(): CommonEvent<boolean> {
    return this.collapsedEmitter.event;
  }

  get minSize(): number {
    const style = getComputedStyle(this.body);
    return parseCssMagnitude(style.minHeight, 0);
  }

  protected onResize(msg: Widget.ResizeMessage): void {
    if (this.wrapped.isAttached && !this.collapsed) {
      MessageLoop.sendMessage(this.wrapped, Widget.ResizeMessage.UnknownSize);
    }
    super.onResize(msg);
  }

  protected onUpdateRequest(msg: Message): void {
    if (this.wrapped.isAttached && !this.collapsed) {
      MessageLoop.sendMessage(this.wrapped, msg);
    }
    super.onUpdateRequest(msg);
  }

  protected onAfterAttach(msg: Message): void {
    if (!this.wrapped.isAttached) {
      UnsafeWidgetUtilities.attach(this.wrapped, this.body);
    }
    this.toolbar && UnsafeWidgetUtilities.attach(this.toolbar, this.header, this.toggleIconBtn);
    super.onAfterAttach(msg);
  }

  protected onBeforeDetach(msg: Message): void {
    super.onBeforeDetach(msg);
    if (this.wrapped.isAttached) {
      UnsafeWidgetUtilities.detach(this.wrapped);
    }
    if (this.toolbar && this.toolbar.isAttached) {
      Widget.detach(this.toolbar);
    }
  }

  protected onBeforeShow(msg: Message): void {
    if (this.wrapped.isAttached && !this.collapsed) {
      MessageLoop.sendMessage(this.wrapped, msg);
    }
    super.onBeforeShow(msg);
  }

  protected onAfterShow(msg: Message): void {
    super.onAfterShow(msg);
    if (this.wrapped.isAttached && !this.collapsed) {
      MessageLoop.sendMessage(this.wrapped, msg);
    }
  }

  protected onBeforeHide(msg: Message): void {
    if (this.wrapped.isAttached && !this.collapsed) {
      MessageLoop.sendMessage(this.wrapped, msg);
    }
    super.onBeforeShow(msg);
  }

  protected onAfterHide(msg: Message): void {
    super.onAfterHide(msg);
    if (this.wrapped.isAttached && !this.collapsed) {
      MessageLoop.sendMessage(this.wrapped, msg);
    }
  }

  protected onChildRemoved(msg: Widget.ChildMessage): void {
    super.onChildRemoved(msg);
    this.dispose();
  }

  protected onActivateRequest(msg: Message): void {
    super.onActivateRequest(msg);
    if (this.collapsed) {
      this.header.focus();
    } else {
      this.wrapped.activate();
    }
  }

}

export namespace ViewContainerPart {

  /**
   * Make sure to adjust the `line-height` of the `.farris-view-container .part > .header` CSS class when modifying this, and vice versa.
   */
  export const HEADER_HEIGHT = 44;

  export interface State {
    widget?: Widget
    partId: string;
    collapsed: boolean;
    hidden: boolean;
    relativeSize?: number;
    description?: string;
  }

  export function closestPart(element: Element | EventTarget | null, selector: string = 'div.part'): Element | undefined {
    if (element instanceof Element) {
      const part = element.closest(selector);
      if (part instanceof Element) {
        return part;
      }
    }
    return undefined;
  }
}

export class ViewContainerLayout extends SplitLayout {

  constructor(protected options: ViewContainerLayout.Options, protected readonly splitPositionHandler: SplitPositionHandler) {
    super(options);
  }

  protected get items(): ReadonlyArray<LayoutItem & ViewContainerLayout.Item> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (this as any)._items as Array<LayoutItem & ViewContainerLayout.Item>;
  }

  iter(): IIterator<ViewContainerPart> {
    return map(this.items, item => item.widget);
  }

  // @ts-expect-error TS2611 `SplitLayout.widgets` is declared as `readonly widgets` but is implemented as a getter.
  get widgets(): ViewContainerPart[] {
    return toArray(this.iter());
  }

  attachWidget(index: number, widget: ViewContainerPart): void {
    super.attachWidget(index, widget);
    if (index > -1 && this.parent && this.parent.node.contains(this.widgets[index + 1] && this.widgets[index + 1].node)) {
      // Set the correct attach index to the DOM elements.
      const ref = this.widgets[index + 1].node;
      this.parent.node.insertBefore(widget.node, ref);
      this.parent.node.insertBefore(this.handles[index], ref);
      this.parent.fit();
    }
  }

  moveWidget(fromIndex: number, toIndex: number, widget: Widget): void {
    const ref = this.widgets[toIndex < fromIndex ? toIndex : toIndex + 1];
    super.moveWidget(fromIndex, toIndex, widget);
    // Keep the order of `_widgets` array just as done before (by `super`) for the `_items` array -
    // to prevent later bugs relying on index.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ArrayExt.move((this as any)._widgets, fromIndex, toIndex);
    if (ref) {
      this.parent!.node.insertBefore(this.handles[toIndex], ref.node);
    } else {
      this.parent!.node.appendChild(this.handles[toIndex]);
    }
    UnsafeWidgetUtilities.detach(widget);
    UnsafeWidgetUtilities.attach(widget, this.parent!.node, this.handles[toIndex]);
  }

  getPartSize(part: ViewContainerPart): number | undefined {
    if (part.collapsed || part.isHidden) {
      return part.uncollapsedSize;
    }
    if (this.orientation === 'horizontal') {
      return part.node.offsetWidth;
    } else {
      return part.node.offsetHeight;
    }
  }

  setPartSizes(weights: (number | undefined)[]): void {
    const parts = this.widgets;
    const availableSize = this.getAvailableSize();

    // Sum up the weights of visible parts
    let totalWeight = 0;
    let weightCount = 0;
    for (let index = 0; index < weights.length && index < parts.length; index++) {
      const part = parts[index];
      const weight = weights[index];
      if (weight && !part.isHidden && !part.collapsed) {
        totalWeight += weight;
        weightCount++;
      }
    }
    if (weightCount === 0 || availableSize === 0) {
      return;
    }

    // Add the average weight for visible parts without weight
    const averageWeight = totalWeight / weightCount;
    for (let index = 0; index < weights.length && index < parts.length; index++) {
      const part = parts[index];
      const weight = weights[index];
      if (!weight && !part.isHidden && !part.collapsed) {
        totalWeight += averageWeight;
      }
    }

    // Apply the weights to compute actual sizes
    let position = 0;
    for (let index = 0; index < weights.length && index < parts.length - 1; index++) {
      const part = parts[index];
      if (!part.isHidden) {
        if (this.orientation === 'vertical') {
          position += this.options.headerSize;
        }
        const weight = weights[index];
        if (part.collapsed) {
          if (weight) {
            part.uncollapsedSize = weight / totalWeight * availableSize;
          }
        } else {
          let contentSize = (weight || averageWeight) / totalWeight * availableSize;
          const minSize = part.minSize;
          if (contentSize < minSize) {
            contentSize = minSize;
          }
          position += contentSize;
        }
        this.setHandlePosition(index, position);
        position += this.spacing;
      }
    }
  }

  /**
   * Determine the size of the split panel area that is available for widget content,
   * i.e. excluding part headers and split handles.
   */
  getAvailableSize(): number {
    if (!this.parent || !this.parent.isAttached) {
      return 0;
    }
    const parts = this.widgets;
    const visiblePartCount = parts.filter(part => !part.isHidden).length;
    let availableSize: number;
    if (this.orientation === 'horizontal') {
      availableSize = this.parent.node.offsetWidth;
    } else {
      availableSize = this.parent.node.offsetHeight;
      availableSize -= visiblePartCount * this.options.headerSize;
    }
    availableSize -= (visiblePartCount - 1) * this.spacing;
    if (availableSize < 0) {
      return 0;
    }
    return availableSize;
  }

  /**
   * Update a view container part that has been collapsed or expanded. The transition
   * to the new state is animated.
   */
  updateCollapsed(part: ViewContainerPart, enableAnimation: boolean): void {
    const index = this.items.findIndex(item => item.widget === part);
    if (index < 0 || !this.parent || part.isHidden) {
      return;
    }

    // Do not store the height of the "stretched item". Otherwise, we mess up the "hint height".
    // Store the height only if there are other expanded items.
    const currentSize = this.orientation === 'horizontal' ? part.node.offsetWidth : part.node.offsetHeight;
    if (part.collapsed && this.items.some(item => !item.widget.collapsed && !item.widget.isHidden)) {
      part.uncollapsedSize = currentSize;
    }

    if (!enableAnimation || this.options.animationDuration <= 0) {
      MessageLoop.postMessage(this.parent, Widget.Msg.FitRequest);
      return;
    }
    let startTime: number | undefined = undefined;
    const duration = this.options.animationDuration;
    const direction = part.collapsed ? 'collapse' : 'expand';
    let fullSize: number;
    if (direction === 'collapse') {
      fullSize = currentSize - this.options.headerSize;
    } else {
      fullSize = Math.max((part.uncollapsedSize || 0) - this.options.headerSize, part.minSize);
      if (this.items.filter(item => !item.widget.collapsed && !item.widget.isHidden).length === 1) {
        // Expand to full available size
        fullSize = Math.max(fullSize, this.getAvailableSize());
      }
    }

    // The update function is called on every animation frame until the predefined duration has elapsed.
    const updateFunc = (time: number) => {
      if (!this.parent) {
        part.animatedSize = undefined;
        return;
      }
      if (startTime === undefined) {
        startTime = time;
      }
      if (time - startTime < duration) {
        // Render an intermediate state for the animation
        const t = this.tween((time - startTime) / duration);
        if (direction === 'collapse') {
          part.animatedSize = (1 - t) * fullSize;
        } else {
          part.animatedSize = t * fullSize;
        }
        requestAnimationFrame(updateFunc);
      } else {
        // The animation is finished
        if (direction === 'collapse') {
          part.animatedSize = undefined;
        } else {
          part.animatedSize = fullSize;
          // Request another frame to reset the part to variable size
          requestAnimationFrame(() => {
            part.animatedSize = undefined;
            if (this.parent) {
              MessageLoop.sendMessage(this.parent, Widget.Msg.FitRequest);
            }
          });
        }
      }
      MessageLoop.sendMessage(this.parent, Widget.Msg.FitRequest);
    };
    requestAnimationFrame(updateFunc);
  }

  protected onFitRequest(msg: Message): void {
    for (const part of this.widgets) {
      const style = part.node.style;
      if (part.animatedSize !== undefined) {
        // The part size has been fixed for animating the transition to collapsed / expanded state
        const fixedSize = `${this.options.headerSize + part.animatedSize}px`;
        style.minHeight = fixedSize;
        style.maxHeight = fixedSize;
      } else if (part.collapsed) {
        // The part size is fixed to the header size
        const fixedSize = `${this.options.headerSize}px`;
        style.minHeight = fixedSize;
        style.maxHeight = fixedSize;
      } else {
        const minSize = `${this.options.headerSize + part.minSize}px`;
        style.minHeight = minSize;
        style.maxHeight = '';
      }
    }
    super.onFitRequest(msg);
  }

  /**
   * Sinusoidal tween function for smooth animation.
   */
  protected tween(t: number): number {
    return 0.5 * (1 - Math.cos(Math.PI * t));
  }

  setHandlePosition(index: number, position: number): Promise<void> {
    const options: SplitPositionOptions = {
      referenceWidget: this.widgets[index],
      duration: 0
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.splitPositionHandler.setSplitHandlePosition(this.parent as SplitPanel, index, position, options) as Promise<any>;
  }

}

export namespace ViewContainerLayout {

  export interface Options extends SplitLayout.IOptions {
    headerSize: number;
    animationDuration: number;
  }

  export interface Item {
    readonly widget: ViewContainerPart;
  }

}
